// Compositor de correo — modal para redactar / responder / reenviar.
// Adjuntos en base64, envío + guardar borrador. Diseño con tokens del tema.

import { useState, type ReactNode } from 'react';
import { Paperclip, Save, Send, X } from 'lucide-react';

import { saveDraft, sendMail } from '../api';
import type { ComposeDraft } from '../types';

export interface ComposePreset {
  to?: string;
  cc?: string;
  subject?: string;
  body?: string;
  inReplyTo?: string;
}

const MAX_ATT_BYTES = 8 * 1024 * 1024; // 8MB por adjunto

function fileToB64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const res = String(r.result || '');
      resolve(res.includes(',') ? res.split(',')[1] : res);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function ComposeModal({
  preset, onClose, onSent,
}: {
  preset?: ComposePreset;
  onClose: () => void;
  onSent: () => void;
}) {
  const [to, setTo] = useState(preset?.to ?? '');
  const [cc, setCc] = useState(preset?.cc ?? '');
  const [showCc, setShowCc] = useState(Boolean(preset?.cc));
  const [subject, setSubject] = useState(preset?.subject ?? '');
  const [body, setBody] = useState(preset?.body ?? '');
  const [atts, setAtts] = useState<ComposeDraft['attachments']>([]);
  const [busy, setBusy] = useState<'' | 'send' | 'draft'>('');
  const [error, setError] = useState<string | null>(null);

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    const next: ComposeDraft['attachments'] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_ATT_BYTES) { setError(`"${f.name}" supera 8MB.`); continue; }
      next.push({ name: f.name, content_type: f.type || 'application/octet-stream', b64: await fileToB64(f) });
    }
    setAtts((prev) => [...prev, ...next]);
  };

  const draft = (): ComposeDraft => ({ to, cc, bcc: '', subject, body, in_reply_to: preset?.inReplyTo, attachments: atts });

  const handleSend = async () => {
    if (!to.trim()) { setError('Falta el destinatario.'); return; }
    setBusy('send'); setError(null);
    try { await sendMail(draft()); onSent(); onClose(); }
    catch (e: any) { setError(e?.message || 'No se pudo enviar.'); }
    finally { setBusy(''); }
  };

  const handleDraft = async () => {
    setBusy('draft'); setError(null);
    try { await saveDraft({ to, cc, bcc: '', subject, body, attachments: atts }); onClose(); }
    catch (e: any) { setError(e?.message || 'No se pudo guardar.'); }
    finally { setBusy(''); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div className="flex h-[85vh] w-full max-w-2xl flex-col rounded-t-2xl border border-border bg-card shadow-2xl sm:h-[70vh] sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">
            {preset?.inReplyTo ? 'Responder' : 'Nuevo correo'}
          </h2>
          <button onClick={onClose} className="rounded-lg p-1 text-muted-foreground hover:bg-muted">
            <X size={18} />
          </button>
        </div>

        {/* Campos */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="space-y-1 border-b border-border px-4 py-2">
            <Field label="Para">
              <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="correo@ejemplo.com"
                className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground" />
              {!showCc && (
                <button onClick={() => setShowCc(true)} className="text-xs text-primary hover:underline">CC</button>
              )}
            </Field>
            {showCc && (
              <Field label="CC">
                <input value={cc} onChange={(e) => setCc(e.target.value)}
                  className="w-full bg-transparent text-sm text-foreground outline-none" />
              </Field>
            )}
            <Field label="Asunto">
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-transparent text-sm text-foreground outline-none" />
            </Field>
          </div>

          <textarea value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="Escribe tu mensaje…"
            className="min-h-0 flex-1 resize-none bg-transparent px-4 py-3 text-sm leading-relaxed text-foreground outline-none placeholder:text-muted-foreground" />

          {atts.length > 0 && (
            <div className="flex flex-wrap gap-2 border-t border-border px-4 py-2">
              {atts.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2 py-1 text-xs text-foreground">
                  <Paperclip size={12} /> {a.name}
                  <button onClick={() => setAtts((prev) => prev.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {error && <div className="border-t border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">{error}</div>}

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">
              <Paperclip size={14} /> Adjuntar
              <input type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
            </label>
            <button onClick={handleDraft} disabled={busy !== ''}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted disabled:opacity-50">
              <Save size={14} /> {busy === 'draft' ? 'Guardando…' : 'Borrador'}
            </button>
          </div>
          <button onClick={handleSend} disabled={busy !== ''}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-1.5 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50">
            <Send size={14} /> {busy === 'send' ? 'Enviando…' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-b border-border/50 py-1 last:border-0">
      <span className="w-14 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex flex-1 items-center gap-2">{children}</div>
    </div>
  );
}
